// Utility for API calls. You can use fetch or replace with axios if installed later.
// import { useNavigate } from 'react-router-dom';
import API_URL from '../apiUrl';
import checkAuth, { redirectToLogin } from './AuthCheck';

function getAuthHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

/**
 * fetch() for this module. When a request that carried a token comes back 401 or
 * 403, the stored token is no longer accepted — expired, revoked, or signed with
 * a server key that has since changed. Sign the user out and send them to the
 * login page, instead of letting every caller surface its own generic
 * "Failed to ..." message while the admin panel sits there looking usable.
 *
 * Public endpoints never send an Authorization header, so they are untouched.
 */
export async function apiFetch(input, init) {
  const response = await fetch(input, init);
  const sentToken = init && init.headers && init.headers.Authorization;
  if (sentToken && (response.status === 401 || response.status === 403)) {
    redirectToLogin();
  }
  return response;
}

export async function fetchEvents() {
  const response = await apiFetch(`${API_URL}/api/events/`, {
    headers: {
      ...getAuthHeaders(),
    },
  });
  if (!response.ok) throw new Error("Failed to fetch events");
  return response.json();
}

export async function addEvent(event) {
  const response = await apiFetch(`${API_URL}/api/events/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify(event),
  });
  if (!response.ok) throw new Error("Failed to add event");
  return response.json();
}

export async function deleteEvent(id) {
  const response = await apiFetch(`${API_URL}/api/events/delete/${id}/`, {
    method: "DELETE",
    headers: {
      ...getAuthHeaders(),
    },
  });
  if (!response.ok) throw new Error("Failed to delete event");
  return true;
}

export async function fetchSubscriptions() {
  const response = await apiFetch(`${API_URL}/api/subscriptions/`, {
    headers: {
      ...getAuthHeaders(),
    },
  });
  if (!response.ok) throw new Error("Failed to fetch subscriptions");
  return response.json();
}

export async function fetchContactForms() {
  const response = await apiFetch(`${API_URL}/api/contact-forms/`, {
    headers: {
      ...getAuthHeaders(),
    },
  });
  if (!response.ok) throw new Error("Failed to fetch contact forms");
  return response.json();
}

export async function fetchContacts() {
  const response = await apiFetch(`${API_URL}/api/contacts/`, {
    headers: {
      ...getAuthHeaders(),
    },
  });
  if (!response.ok) throw new Error("Failed to fetch contacts");
  return response.json();
}

export async function submitContact(contact) {
  const response = await apiFetch(`${API_URL}/api/contact-forms/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(contact),
  });
  if (!response.ok) throw new Error("Failed to submit contact form");
  return response.json();
}

export function isLoggedIn() {
  // Expiry-aware: a leftover token must not make the UI claim you're signed in.
  return checkAuth();
}

export function logout(navigate) {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  if (navigate) navigate('/');
}

// Endorsements API
export async function fetchEndorsements() {
  const response = await apiFetch(`${API_URL}/api/endorsements/`);
  if (!response.ok) throw new Error("Failed to fetch endorsements");
  return response.json();
}

export async function addEndorsement(formData) {
  const response = await apiFetch(`${API_URL}/api/endorsements/`, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
    },
    body: formData,
  });
  if (!response.ok) throw new Error("Failed to add endorsement");
  return response.json();
}

export async function updateEndorsement(id, formData) {
  const response = await apiFetch(`${API_URL}/api/endorsements/${id}/`, {
    method: "PATCH",
    headers: {
      ...getAuthHeaders(),
    },
    body: formData,
  });
  if (!response.ok) throw new Error("Failed to update endorsement");
  return response.json();
}

export async function deleteEndorsement(id) {
  const response = await apiFetch(`${API_URL}/api/endorsements/${id}/`, {
    method: "DELETE",
    headers: {
      ...getAuthHeaders(),
    },
  });
  if (!response.ok) throw new Error("Failed to delete endorsement");
  return true;
}

// Issues API
export async function fetchIssues() {
  const response = await apiFetch(`${API_URL}/api/issues/`, {
    headers: {
      ...getAuthHeaders(),
    },
  });
  if (!response.ok) throw new Error("Failed to fetch issues");
  return response.json();
}

export async function addIssue(formData) {
  const response = await apiFetch(`${API_URL}/api/issues/`, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
    },
    body: formData,
  });
  if (!response.ok) throw new Error("Failed to add issue");
  return response.json();
}

export async function deleteIssue(id) {
  const response = await apiFetch(`${API_URL}/api/issues/${id}/`, {
    method: "DELETE",
    headers: {
      ...getAuthHeaders(),
    },
  });
  if (!response.ok) throw new Error("Failed to delete issue");
  return true;
}

export async function updateIssue(id, formData) {
  const response = await apiFetch(`${API_URL}/api/issues/${id}/`, {
    method: "PATCH",
    headers: {
      ...getAuthHeaders(),
    },
    body: formData,
  });
  if (!response.ok) throw new Error("Failed to update issue");
  return response.json();
}

// Site settings
export async function fetchSiteSettings() {
  const res = await apiFetch(`${API_URL}/api/site-settings/`);
  if (!res.ok) return null;
  const data = await res.json();
  return Array.isArray(data) ? data[0] : data;
}

export async function upsertSiteSetting(payload) {
  // Try to fetch existing
  const existing = await fetchSiteSettings();
  if (existing && existing.id) {
    const res = await apiFetch(`${API_URL}/api/site-settings/${existing.id}/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to update site settings');
    return res.json();
  } else {
    const res = await apiFetch(`${API_URL}/api/site-settings/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to create site settings');
    return res.json();
  }
}

// Videos API
export async function fetchVideos() {
  const res = await apiFetch(`${API_URL}/api/videos/`);
  if (!res.ok) throw new Error('Failed to fetch videos');
  return res.json();
}

export async function fetchVideo(id) {
  const res = await apiFetch(`${API_URL}/api/videos/${id}/`);
  if (!res.ok) throw new Error('Failed to fetch video');
  return res.json();
}

export async function addVideo(formData) {
  const res = await apiFetch(`${API_URL}/api/videos/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  });
  if (!res.ok) throw new Error('Failed to add video');
  return res.json();
}

export async function deleteVideo(id) {
  const res = await apiFetch(`${API_URL}/api/videos/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to delete video');
  return true;
}

export async function updateVideo(id, formData) {
  const res = await apiFetch(`${API_URL}/api/videos/${id}/`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: formData,
  });
  if (!res.ok) throw new Error('Failed to update video');
  return res.json();
}

// Countdowns
export async function fetchCountdowns() {
  const res = await apiFetch(`${API_URL}/api/countdowns/`);
  if (!res.ok) return [];
  return res.json();
}

export async function createCountdown(payload) {
  const res = await apiFetch(`${API_URL}/api/countdowns/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Failed to create countdown");
  return res.json();
}

export async function updateCountdown(id, payload) {
  const res = await apiFetch(`${API_URL}/api/countdowns/${id}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Failed to update countdown");
  return res.json();
}

export async function deleteCountdown(id) {
  const res = await apiFetch(`${API_URL}/api/countdowns/${id}/`, {
    method: 'DELETE',
    headers: { ...getAuthHeaders() },
  });
  if (!res.ok) throw new Error("Failed to delete countdown");
  return true;
}